import React, { useEffect, useState } from 'react';
import { ChevronDown, Globe, Loader2, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { categoriesAPI, i18nAPI } from '../services/api';
import { TARGET_LANGS } from './TranslationsEditor';

const FIELDS = [['seo_title', 'Titre SEO', 120], ['meta_description', 'Méta-description', 320], ['h1', 'Titre H1', 160], ['excerpt', 'Introduction', 800], ['main_keyword', 'Mot-clé principal', 80]];

// Per-language SEO translations of a category page (title, meta, H1, intro, guide, FAQ) + AI job per category
const SeoTranslationsPanel = ({ form, setForm, categoryId }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('en');
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const hasSeo = !!(form.h1 || form.seo_title || form.description || form.excerpt);
  const seo = form.seo_i18n || {};
  const filled = TARGET_LANGS.filter(l => seo[l.code]?.h1 || seo[l.code]?.seo_title).length;
  const subDone = Object.keys(form.subcategory_seo_i18n?.[tab] || {}).length;
  const subTotal = Object.values(form.subcategory_seo || {}).filter(v => v && Object.values(v).some(Boolean)).length;

  const setField = (key, value) => setForm(f => ({ ...f, seo_i18n: { ...(f.seo_i18n || {}), [tab]: { ...((f.seo_i18n || {})[tab] || {}), [key]: value } } }));
  const setFaq = (i, key, value) => setForm(f => {
    const faq = [...(((f.seo_i18n || {})[tab] || {}).faq || (f.faq || []).map(() => ({ question: '', answer: '' })))];
    faq[i] = { ...(faq[i] || { question: '', answer: '' }), [key]: value };
    return { ...f, seo_i18n: { ...(f.seo_i18n || {}), [tab]: { ...((f.seo_i18n || {})[tab] || {}), faq } } };
  });

  useEffect(() => {
    if (!job?.running) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await i18nAPI.status();
        const j = st.jobs?.seo;
        setJob(j);
        if (j && !j.running) {
          clearInterval(id);
          const fresh = (await categoriesAPI.getAll()).find(c => c.id === categoryId);
          if (fresh) setForm(f => ({ ...f, seo_i18n: fresh.seo_i18n || {}, subcategory_seo_i18n: fresh.subcategory_seo_i18n || {} }));
          if (j.errors?.length) setError(j.errors.join(' ; '));
        }
      } catch { /* retry */ }
    }, 3000);
    return () => clearInterval(id);
  }, [job?.running, categoryId, setForm]);

  const translate = async (overwrite) => {
    if (overwrite && !window.confirm('Remplacer les traductions SEO existantes de cette catégorie par une nouvelle version IA ?')) return;
    setError(null);
    try {
      const r = await i18nAPI.translateSeo(categoryId, overwrite);
      if (!r.started) { setError(r.message); return; }
      setJob(r.job);
    } catch (e) { setError(e.response?.data?.detail || 'Traduction impossible'); }
  };

  const faqSrc = form.faq || [];
  const faqTr = seo[tab]?.faq || [];
  return (
    <div className="border border-gray-200 rounded-lg" data-testid="category-seo-translations">
      <button type="button" data-testid="category-seo-translations-toggle" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Globe size={15} className="text-violet-600" /> SEO multilingue (EN / DE / ES / IT)</span>
        <span className="flex items-center gap-2">
          <span data-testid="category-seo-translations-count" className={`text-xs font-bold px-2 py-0.5 rounded-full ${filled === 4 ? 'bg-sky-100 text-sky-700' : filled ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{filled}/4 langues</span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-xs text-gray-600 flex-1">{categoryId ? 'Traduit le titre SEO, la méta-description, le H1, l\'introduction, le guide, la FAQ et les extraits des sous-catégories à partir du français (enregistré automatiquement). À relire.' : 'Enregistrez d\'abord la catégorie pour lancer la traduction IA du SEO.'}</p>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" data-testid="category-seo-translate-button" onClick={() => translate(false)} disabled={!categoryId || !hasSeo || job?.running} className="border-violet-400 text-violet-700 hover:bg-violet-100">
                {job?.running ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Wand2 size={13} className="mr-1" />} {job?.running ? 'Traduction en cours…' : 'Traduire le SEO (IA)'}
              </Button>
              {filled > 0 && !job?.running && <Button type="button" size="sm" variant="ghost" data-testid="category-seo-retranslate-button" onClick={() => translate(true)} disabled={!categoryId} className="text-xs text-gray-500">Regénérer</Button>}
            </div>
          </div>
          {error && <p className="text-xs text-red-600" data-testid="category-seo-translations-error">{error}</p>}
          <div className="flex flex-wrap border-b" role="tablist">
            {TARGET_LANGS.map(l => (
              <button key={l.code} type="button" role="tab" onClick={() => setTab(l.code)} data-testid={`category-seo-translations-tab-${l.code}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border-b-2 -mb-px transition-colors ${tab === l.code ? 'border-violet-500 text-violet-700 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                <img src={`https://flagcdn.com/w20/${l.flag}.png`} alt="" className="w-4 h-3 rounded-sm no-placeholder" /> {l.label}
                {(seo[l.code]?.h1 || seo[l.code]?.seo_title) && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map(([key, label, max]) => (
              <label key={key} className={`block ${key === 'excerpt' ? 'md:col-span-2' : ''}`}>
                <span className="block text-xs font-medium text-gray-600 mb-1">{label} <span className="font-normal text-gray-400">· FR : {(form[key] || '—').slice(0, 70)}{(form[key] || '').length > 70 ? '…' : ''}</span></span>
                <Input value={seo[tab]?.[key] || ''} onChange={(e) => setField(key, e.target.value)} maxLength={max} className="h-8 text-sm" data-testid={`category-seo-translations-${tab}-${key}`} />
              </label>
            ))}
            <label className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-600 mb-1">Guide / description (markdown) <span className="font-normal text-gray-400">· {(seo[tab]?.description || '').length} car.</span></span>
              <Textarea value={seo[tab]?.description || ''} onChange={(e) => setField('description', e.target.value)} rows={6} maxLength={12000} className="text-sm font-mono" data-testid={`category-seo-translations-${tab}-description`} />
            </label>
          </div>
          {faqSrc.length > 0 && (
            <div className="space-y-2" data-testid={`category-seo-translations-${tab}-faq`}>
              <p className="text-xs font-medium text-gray-600">FAQ ({faqSrc.length} question{faqSrc.length > 1 ? 's' : ''})</p>
              {faqSrc.map((q, i) => (
                <div key={i} className="rounded-md border border-gray-100 p-2 space-y-1">
                  <p className="text-[11px] text-gray-400 truncate">FR : {q.question}</p>
                  <Input value={faqTr[i]?.question || ''} onChange={(e) => setFaq(i, 'question', e.target.value)} placeholder="Question traduite" maxLength={300} className="h-8 text-sm" data-testid={`category-seo-translations-${tab}-faq-${i}-question`} />
                  <Textarea value={faqTr[i]?.answer || ''} onChange={(e) => setFaq(i, 'answer', e.target.value)} placeholder="Réponse traduite" rows={2} maxLength={2000} className="text-sm" data-testid={`category-seo-translations-${tab}-faq-${i}-answer`} />
                </div>
              ))}
            </div>
          )}
          {subTotal > 0 && <p className="text-xs text-gray-500" data-testid={`category-seo-translations-${tab}-subs`}>Extraits SEO des sous-catégories traduits en {TARGET_LANGS.find(l => l.code === tab)?.label} : <strong>{subDone}/{subTotal}</strong> (générés par l'IA, non modifiables ici).</p>}
        </div>
      )}
    </div>
  );
};

export default SeoTranslationsPanel;
